import { useEffect, useState } from "react";
import { toTitleCase } from "../../../utils/toTitleCase";
import { cleanDispositionString } from "./utils";
import { CardUT } from "./CardUT";
import {
  AUTO_EXPUNGEMENT,
  ELIGIBLE,
  EXPUNGEMENT,
  FELONY,
  INELIGIBLE,
  INFRACTION,
  MISDEMEANOR,
  PARKING,
  TRAFFIC,
  UNKNOWN,
} from "./results";

const dateOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

export const compare = (a, b) => {
  if (a.props.data.dispositionDate > b.props.data.dispositionDate) {
    return -1;
  }
  if (a.props.data.dispositionDate < b.props.data.dispositionDate) {
    return 1;
  }
  return 0;
};

export const CardSectionUT = ({
  data,
  ineligibleReasons,
  openCases,
  openMessageUs,
  onProbationOrParole,
  receivedAnyMessage,
  setIneligibleReasons,
  setOneIsEligible,
  setOpenMessageUs,
}) => {
  const [misdemeanorCards, setMisdemeanorCards] = useState([]);
  const [felonyCards, setFelonyCards] = useState([]);
  const [infractionCards, setInfractionCards] = useState([]);
  const [trafficCards, setTrafficCards] = useState([]);

  useEffect(() => {
    const missCards = [];
    const felCards = [];
    const trafCards = [];
    const infracCards = [];
    // eslint-disable-next-line no-console
    console.log("data before data.map", data);
    // in the record obj, we get each remedy that is eligible,
    // then we get the lead charge within that record
    data.map((record) => {
      let expungementEligibleDate = "";
      let expungementEligibleNow = false;
      let expungementEligibleLater = false;
      let autoExpungementEligibleDate = "";
      let autoExpungementEligibleNow = false;
      let autoExpungementEligibleLater = false;
      let needMoreInfo = false;

      let leadChargeNumber = record.lead_charge;
      let leadCharge = record.charges.find((charge) => charge.count_number === leadChargeNumber);
      let reasons = "";
      leadCharge.remedies.forEach((remedy) => {
        const today = new Date();
        if (remedy.type.toLowerCase() === EXPUNGEMENT) {
          const eligibleBy = remedy.eligible_by ? new Date(remedy.eligible_by) : null;
          if (remedy.eligibility.toLowerCase() === INELIGIBLE) {
            expungementEligibleNow = false;
            reasons = remedy.reasons.join(", ");
            let globalReasonsString = ineligibleReasons.slice();
            remedy.reasons.forEach((reason) => {
              const reasonLower = reason.toLowerCase();
              if (reasonLower.includes("too many") || reasonLower.includes("has open case")) {
                if (!globalReasonsString.includes(reason)) {
                  if (globalReasonsString === "") {
                    globalReasonsString += reason;
                  } else {
                    globalReasonsString += ", " + reason;
                  }
                }
              }
            });
            setIneligibleReasons(globalReasonsString);
            if (globalReasonsString === "" && eligibleBy > today) {
              expungementEligibleLater = true;
              expungementEligibleDate = eligibleBy.toLocaleDateString("en-US", dateOptions);
            }
          } else if (remedy.eligibility.toLowerCase() === ELIGIBLE) {
            expungementEligibleNow = true;
            if (!openCases || !onProbationOrParole) {
              setOneIsEligible(true);
            }
          } else if (remedy.eligibility.toLowerCase() === UNKNOWN) {
            needMoreInfo = true;
          }
        } else if (remedy.type.toLowerCase() === AUTO_EXPUNGEMENT) {
          const eligibleBy = remedy.eligible_by ? new Date(remedy.eligible_by) : null;
          if (remedy.eligibility.toLowerCase() === INELIGIBLE) {
            autoExpungementEligibleNow = false;
            reasons = remedy.reasons.join(", ");
          } else if (remedy.eligibility.toLowerCase() === ELIGIBLE) {
            autoExpungementEligibleNow = true;
          } else if (eligibleBy > today) {
            autoExpungementEligibleLater = true;
            autoExpungementEligibleDate = eligibleBy.toLocaleDateString("en-US", dateOptions);
          }
        }
      });

      const cardData = {
        caseNumber: record.case_number,
        court: record.court,
        violation: leadCharge.description
          ? toTitleCase(leadCharge.description.toLowerCase())
          : "--",
        violationCode: record.code,
        disposition: leadCharge.disposition ? cleanDispositionString(leadCharge.disposition) : "--",
        dispositionDate: leadCharge.disposition_date
          ? toTitleCase(leadCharge.disposition_date.toLowerCase())
          : "--",
        severity: leadCharge.severity ? leadCharge.severity.toUpperCase() : "--",
        charges: record.charges,
        expungementEligibleDate: expungementEligibleDate,
        expungementEligibleNow: expungementEligibleNow,
        expungementEligibleLater: expungementEligibleLater,
        autoExpungementEligibleDate: autoExpungementEligibleDate,
        autoExpungementEligibleNow: autoExpungementEligibleNow,
        autoExpungementEligibleLater: autoExpungementEligibleLater,
        reasons: reasons,
        needMoreInfo: needMoreInfo,
      };

      const currentCard = (
        <CardUT
          key={cardData.caseNumber}
          data={cardData}
          openMessageUs={openMessageUs}
          setOpenMessageUs={setOpenMessageUs}
          openCases={openCases}
          onProbationOrParole={onProbationOrParole}
          receivedAnyMessage={receivedAnyMessage}
        />
      );

      if (
        record.case_type?.toLowerCase().includes(TRAFFIC) ||
        record.case_type?.toLowerCase().includes(PARKING)
      ) {
        trafCards.push(currentCard);
      } else if (leadCharge.severity_type.toLowerCase().includes(INFRACTION)) {
        infracCards.push(currentCard);
      } else if (leadCharge.severity_type.toLowerCase().includes(MISDEMEANOR)) {
        missCards.push(currentCard);
      } else if (leadCharge.severity_type.toLowerCase().includes(FELONY)) {
        felCards.push(currentCard);
      }
    });

    const felSorted = felCards.sort(compare);
    const misSorted = missCards.sort(compare);
    const infracSorted = infracCards.sort(compare);
    const trafSorted = trafCards.sort(compare);

    setFelonyCards(felSorted);
    setMisdemeanorCards(misSorted);
    setInfractionCards(infracSorted);
    setTrafficCards(trafSorted);
  }, [
    openMessageUs,
    data,
    openCases,
    onProbationOrParole,
    receivedAnyMessage,
    ineligibleReasons,
    setIneligibleReasons,
    setOneIsEligible,
    setOpenMessageUs,
  ]);

  return (
    <>
      <section className="bg-pink-100 pb-12 pt-1">
        <div className="container mx-auto mt-10 flex max-w-3xl flex-col px-4">
          <h3 className="mb-1 font-serif text-2xl">Felony Records</h3>
          <div>
            We found {felonyCards.length === 0 ? "no" : felonyCards.length} felony{" "}
            {felonyCards.length === 1 ? "record" : "records"}
          </div>
          {felonyCards.map((card) => card)}
        </div>
      </section>
      <section className="bg-gray-200 pb-12 pt-1">
        <div className="container mx-auto mt-10 flex max-w-3xl flex-col px-4">
          <h3 className="mb-1 font-serif text-2xl">Misdemeanor Records</h3>
          <div>
            We found {misdemeanorCards.length === 0 ? "no" : misdemeanorCards.length} misdemeanor{" "}
            {misdemeanorCards.length === 1 ? "record" : "records"}
          </div>
          {misdemeanorCards.map((card) => card)}
        </div>
      </section>
      <section className="bg-gray-50 pb-12 pt-1">
        <div className="container mx-auto mt-10 flex max-w-3xl flex-col px-4">
          <h3 className="mb-1 font-serif text-2xl">Infraction Records</h3>
          <div>
            We found {infractionCards.length === 0 ? "no" : infractionCards.length} infraction{" "}
            {infractionCards.length === 1 ? "record" : "records"}
          </div>
          {infractionCards.map((card) => card)}
        </div>
      </section>
      <section className="bg-blue-50 pb-12 pt-1">
        <div className="container mx-auto mt-10 flex max-w-3xl flex-col px-4">
          <h3 className="mb-1 font-serif text-2xl">Traffic Records</h3>
          <div>
            We found {trafficCards.length === 0 ? "no" : trafficCards.length} traffic{" "}
            {trafficCards.length === 1 ? "record" : "records"}
          </div>
          {trafficCards.map((card) => card)}
        </div>
      </section>
    </>
  );
};
